const VERSION_PATTERN = /^(\d+)\.(\d+)\.(\d+)(?:\.(\d+))?$/;

export interface ParsedPluginVersion {
  major: number;
  minor: number;
  patch: number;
  build?: number;
}

export function parsePluginVersion(
  version: string | null | undefined,
): ParsedPluginVersion | null {
  if (!version) {
    return null;
  }

  const trimmed = version.trim();
  const match = VERSION_PATTERN.exec(trimmed);
  if (!match) {
    return null;
  }

  const major = Number.parseInt(match[1], 10);
  const minor = Number.parseInt(match[2], 10);
  const patch = Number.parseInt(match[3], 10);
  const build =
    match[4] === undefined ? undefined : Number.parseInt(match[4], 10);

  if (
    Number.isNaN(major) ||
    Number.isNaN(minor) ||
    Number.isNaN(patch) ||
    Number.isNaN(build) ||
    major < 0 ||
    minor < 0 ||
    patch < 0 ||
    (build !== undefined && build < 0)
  ) {
    return null;
  }

  return { major, minor, patch, ...(build !== undefined ? { build } : {}) };
}

export function comparePluginVersions(
  left: ParsedPluginVersion,
  right: ParsedPluginVersion,
): number {
  if (left.major !== right.major) {
    return left.major > right.major ? 1 : -1;
  }
  if (left.minor !== right.minor) {
    return left.minor > right.minor ? 1 : -1;
  }
  if (left.patch !== right.patch) {
    return left.patch > right.patch ? 1 : -1;
  }

  const leftBuild = left.build ?? 0;
  const rightBuild = right.build ?? 0;
  if (leftBuild !== rightBuild) {
    return leftBuild > rightBuild ? 1 : -1;
  }

  return 0;
}

export function isPluginVersionAtLeast(
  pluginVersion: string | null | undefined,
  minimumVersion: string,
): boolean {
  const parsedPlugin = parsePluginVersion(pluginVersion);
  const parsedMinimum = parsePluginVersion(minimumVersion);

  if (!parsedPlugin || !parsedMinimum) {
    return false;
  }

  return comparePluginVersions(parsedPlugin, parsedMinimum) >= 0;
}
